import React, {useEffect, useRef, useState} from 'react';
import {
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    IconButton,
    Typography,
    Tabs,
    Tab,
    Paper, Grid, FormHelperText, FormControlLabel, Checkbox, Chip
} from '@mui/material';
import {Edit as EditIcon, Add as AddIcon, NotificationsActive} from '@mui/icons-material';
import EmojiPicker from "emoji-picker-react";
import { ReactComponent as PhoneSvg } from '../../imgs/phone.svg';
import logoImg from '../../imgs/icon2.png';

// Справочник языков
const LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'ru', name: 'Russian' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'pt_br', name: 'Português do Brasil' },
    { code: 'nl', name: 'Nederlands' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'zh', name: 'Chinese' },
    { code: 'zh_tw', name: 'ChineseTaiwan' },
    { code: 'ukUA', name: 'Ukrainian' },
    { code: 'plPL', name: 'Polish' },
];


const MAX_TITLE_LENGTH = 40;
const MAX_BODY_LENGTH = 120;


export default function LanguageForm({messages, setMessages, hasError}) {


    const [currentTab, setCurrentTab] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showEmojiPickerMessage, setShowEmojiPickerMessage] = useState(false);
    const [pickerAnchorEl, setPickerAnchorEl] = useState(null);

    // Refs для обработки кликов вне emoji picker
    const emojiTitleRef = useRef(null);
    const emojiMessageRef = useRef(null);


    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handleEmojiClick = (emojiObject, field) => {
        const newValue = currentData[field] + emojiObject.emoji;
        handleFieldChange(field, newValue);
        setShowEmojiPicker(false);
        setShowEmojiPickerMessage(false);
    };

    const handleLanguageToggle = (languageCode, isChecked) => {
        if (isChecked) {
            // Добавление языка
            const language = LANGUAGES.find(lang => lang.code === languageCode);
            if (language && !messages.find(item => item.locale === languageCode)) {

                const defaultTitle = '';
                const defaultBody = '';

                const newFormData = [...messages, {
                    locale: languageCode,
                    title: defaultTitle,
                    body: defaultBody
                }];
                setMessages(newFormData);
                setCurrentTab(newFormData.length - 1);
            }
        } else {
            // Удаление языка (кроме английского)
            if (languageCode !== 'en') {
                const updatedMessages = messages.filter(msg => msg.locale !== languageCode);
                setMessages(updatedMessages);
                if (currentTab >= updatedMessages.length) {
                    setCurrentTab(Math.max(0, updatedMessages.length - 1));
                }
            }
        }
    };


    const handleFieldChange = (field, value) => {
        const newFormData = [...messages];
        newFormData[currentTab] = { ...newFormData[currentTab], [field]: value };
        setMessages(newFormData);
    };

    const getCurrentLanguageName = () => {
        const currentItem = messages[currentTab];
        const language = LANGUAGES.find(lang => lang.code === currentItem.locale);
        return language ? language.name : currentItem.locale;
    };

    const getTabLabel = (item) => {
        const language = LANGUAGES.find(lang => lang.code === item.locale);
        return language ? language.name : item.locale;
    };

    const currentData = messages[currentTab] || { locale: 'en', title: '', body: '' };

    // Обработка кликов вне emoji picker
    useEffect(() => {
        const handleClickOutside = (event) => {
            debugger
            if (showEmojiPicker && emojiTitleRef.current && !emojiTitleRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }

            if (showEmojiPickerMessage && emojiMessageRef.current && !emojiMessageRef.current.contains(event.target)) {
                setShowEmojiPickerMessage(false);
            }

        };

        if (showEmojiPicker || showEmojiPickerMessage) {
            document.addEventListener('mousedown', handleClickOutside);

            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }

    }, [showEmojiPicker, showEmojiPickerMessage]);

    const isLanguageIncomplete = (item) => {
        return !item.title || !item.body || item.title.trim() === '' || item.body.trim() === '';
    };

    const isFieldEmpty = (value) => {
        return !value || value.trim() === '';
    };

    const renderCharacterCounter = (text, limit) => {
        const length = text ? text.length : 0;
        const isOverflow = length > limit;
        return (
            <Typography
                component="span"
                variant="caption"
                sx={{
                    color: isOverflow ? '#fbc02d' : 'text.secondary', // Yellow warning if overflow
                    fontWeight: isOverflow ? 'bold' : 'normal',
                    display: 'block',
                    textAlign: 'right'
                }}
            >
                {length}/{limit}
            </Typography>
        );
    };


    return (
        <Grid container spacing={3}>
            <Grid item size={6}>
                <Box sx={{ width: '100%', mx: 'auto' }}>
                    {/* Tabs */}
                    <Paper elevation={1} sx={{ mb: 3 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            borderBottom: 1,
                            borderColor: 'divider',
                            p: 2
                        }}>
                            <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                flexGrow: 1
                            }}>
                                {messages.map((item, index) => (
                                    <Chip
                                        key={index}
                                        label={getTabLabel(item)}
                                        variant={currentTab === index ? "filled" : "outlined"}
                                        color={currentTab === index ? "primary" : "default"}
                                        onClick={() => handleTabChange(null, index)}
                                        clickable
                                        sx={{
                                            ...(isLanguageIncomplete(item) && {
                                                border: '1px solid #f00 !important',
                                                '&.MuiChip-outlined': {
                                                    borderColor: '#f00 !important'
                                                },
                                                '&.MuiChip-filled': {
                                                    borderColor: '#f00 !important'
                                                }
                                            })
                                        }}

                                    />
                                ))}
                            </Box>
                            <IconButton
                                onClick={() => setDialogOpen(true)}
                                color="primary"
                            >
                                <EditIcon />
                            </IconButton>
                        </Box>


                    </Paper>

                    {/* Form Fields */}
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Title
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder={`Title (${getCurrentLanguageName()})`}
                            value={currentData.title}
                            onChange={(e) => handleFieldChange('title', e.target.value)}
                            sx={{ mb: 3 }}
                            error={isFieldEmpty(currentData.title)}
                            helperText={renderCharacterCounter(currentData.title, MAX_TITLE_LENGTH)}

                            InputProps={{
                                endAdornment: (
                                    <Box sx={{ position: 'relative' }}>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowEmojiPicker((prev) => !prev);
                                                setShowEmojiPickerMessage(false);
                                            }}

                                        >
                                            <AddIcon />
                                        </IconButton>
                                        {showEmojiPicker && (
                                            <Box ref={emojiTitleRef}
                                                 sx={{ position: 'absolute', top: '-150px', zIndex: 100 }}>
                                                <EmojiPicker onEmojiClick={(emoji) => handleEmojiClick(emoji, 'title')} />
                                            </Box>
                                        )}
                                    </Box>
                                )
                            }}
                        />

                        <Typography variant="h6" gutterBottom>
                            Message
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            error={isFieldEmpty(currentData.body)}
                            placeholder={`Message (${getCurrentLanguageName()})`}
                            value={currentData.body}
                            helperText={renderCharacterCounter(currentData.body, MAX_BODY_LENGTH)}

                            onChange={(e) => handleFieldChange('body', e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <Box sx={{ position: 'relative' }}>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowEmojiPickerMessage((prev) => !prev);
                                                setShowEmojiPicker(false);
                                            }}

                                        >
                                            <AddIcon />
                                        </IconButton>
                                        {showEmojiPickerMessage && (
                                            <Box ref={emojiMessageRef} sx={{ position: 'absolute', top: '-350px', zIndex: 100 }}>
                                                <EmojiPicker onEmojiClick={(emoji) => handleEmojiClick(emoji, 'body')} />
                                            </Box>
                                        )}
                                    </Box>
                                )
                            }}
                        />
                    </Box>

                    {/* Language Selection Dialog */}
                    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>Select Languages</DialogTitle>
                        <DialogContent>
                                {LANGUAGES.map((language) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={messages.some(item => item.locale === language.code)}
                                                onChange={(e) => {
                                                    handleLanguageToggle(language.code, e.target.checked);
                                                }}
                                                disabled={language.code === 'en'} // Английский всегда должен быть выбран
                                            />
                                        }
                                        label={
                                            <Box sx={{p:0,m:0, display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <Typography variant="body1" sx={{ minWidth: '200px' }}>
                                                    {language.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                                    {language.code}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ width: '100%', m: 0, p:0  }}
                                    />
                                ))}
                        </DialogContent>
                        <DialogActions>
                            <Button variant={"contained"} color={"warning"} onClick={() => setDialogOpen(false)}>Close</Button>
                        </DialogActions>
                    </Dialog>



                    {hasError && (
                        <FormHelperText error>
                            All selected languages should be filled
                        </FormHelperText>
                    )}

                </Box>
            </Grid>
            <Grid item size={6}>
                <Box sx={{ position: 'relative', width: '500px', height: 'auto' }}>
                    <PhoneSvg style={{ width: '100%', height: 'auto' }} />

                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%', // координаты относительно изображения
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            padding: '12px',
                            borderRadius: '12px',
                            width:'330px',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                            textAlign: 'left',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1.5,
                            alignItems: 'flex-start'
                        }}
                    >
                        {/* App Icon */}
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >

                            <img src={logoImg} alt="Push Notifications"
                                 sx={{maxWidth: '100%', paddingRight: '20px'}}/>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* Preview Title */}
                            <Typography
                                className={"previewTitle"}
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 'bold',
                                    mb: 0,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '100%',
                                    fontSize: '13px',
                                    lineHeight: '1.2'
                                }}
                            >
                                {currentData.title || 'Notification Title'}
                            </Typography>

                            {/* Preview Message */}
                            <Typography
                                className={"previewMessage"}
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    fontSize: '0.85rem',
                                    lineHeight: 1.35
                                }}
                            >
                                {currentData.body && currentData.body.length > MAX_BODY_LENGTH
                                    ? (
                                        <>
                                            {currentData.body.substring(0, MAX_BODY_LENGTH)}...

                                        </>
                                    )
                                    : (currentData.body || 'Notification message description goes here...')
                                }
                            </Typography>
                        </Box>
                    </Box>


                </Box>
            </Grid>
        </Grid>

    );
}